import type {
  CollectionId,
  Database,
  DatabaseId,
  SchemaName,
  TableId,
} from "metabase-types/api";

import type { GroupId } from "./group";
import type { ParameterTarget } from "./parameters";
import type { UserAttributeKey } from "./user";

export enum DataPermission {
  VIEW_DATA = "view-data",
  CREATE_QUERIES = "create-queries",
  DOWNLOAD = "download",
  DATA_MODEL = "data-model",
  DETAILS = "details",
  TRANSFORMS = "transforms",
  COLLECTIONS = "collections",
}

export enum DataPermissionType {
  ACCESS = "access",
  NATIVE = "native",
  DETAILS = "details",
  DOWNLOAD = "download",
  DATA_MODEL = "data-model",
  TRANSFORMS = "transforms",
  COLLECTIONS = "collections",
}

export enum DataPermissionValue {
  BLOCKED = "blocked",
  CONTROLLED = "controlled",
  IMPERSONATED = "impersonated",
  LEGACY_NO_SELF_SERVICE = "legacy-no-self-service",
  NO = "no",
  QUERY_BUILDER = "query-builder",
  QUERY_BUILDER_AND_NATIVE = "query-builder-and-native",
  SANDBOXED = "sandboxed",
  UNRESTRICTED = "unrestricted",
  // download specific values
  NONE = "none",
  LIMITED = "limited",
  FULL = "full",
  // details specific values
  YES = "yes",
  // data model specific values
  ALL = "all",
  //collections
  WRITE = "write",
  READ = "read",
  //NONE = "none", //shared with download above
}

export type PermissionsGraph = {
  groups: GroupsPermissions;
  revision: number;
};

// The `groups`/`revision` pair is always present; advanced-permissions plugins
// (sandboxing, impersonation, …) append their own top-level keys, hence the
// open index signature.
export type UpdatePermissionsGraphRequest = {
  groups: GroupsPermissions;
  revision: number;
  [key: string]: unknown;
};

export type GroupsPermissions = {
  [key: GroupId | string]: GroupPermissions;
};

export type GroupPermissions = {
  [key: DatabaseId]: DatabasePermissions;
};

export type DownloadPermission =
  | DataPermissionValue.FULL
  | DataPermissionValue.LIMITED
  | DataPermissionValue.NONE;

export type DownloadAccessPermission = {
  native?: DownloadSchemasPermission;
  schemas: DownloadSchemasPermission;
};

export type DetailsPermission =
  | DataPermissionValue.NO
  | DataPermissionValue.YES;

export type DetailsPermissions = {
  [key: DatabaseId]: DetailsPermission;
};

export type DownloadSchemasPermission =
  | DownloadPermission
  | { [key: SchemaName]: DownloadTablePermission };

export type DownloadTablePermission =
  | DownloadPermission
  | { [key: TableId]: DownloadPermission };

export type TransformsPermission =
  | DataPermissionValue.NO
  | DataPermissionValue.YES;

export type DatabasePermissions = {
  [DataPermission.VIEW_DATA]: SchemasPermissions;
  [DataPermission.CREATE_QUERIES]?: NativePermissions;
  [DataPermission.DATA_MODEL]?: DataModelPermissions;
  [DataPermission.DOWNLOAD]?: DownloadAccessPermission;
  [DataPermission.DETAILS]?: DetailsPermissions;
  [DataPermission.TRANSFORMS]?: TransformsPermission;
};

export type DataModelPermissions = {
  schemas: SchemasPermissions;
};

export type DatabaseAccessPermissions = {
  native?: NativePermissions;
  schemas: SchemasPermissions;
};

export type NativePermissions =
  | NativePermissionValues
  | { [key: SchemaName]: { [key: TableId]: NativePermissionValues } };

export type NativePermissionValues =
  | DataPermissionValue.QUERY_BUILDER_AND_NATIVE
  | DataPermissionValue.QUERY_BUILDER
  | DataPermissionValue.NO
  | undefined;

export type SchemasPermissions =
  | DataPermissionValue.UNRESTRICTED
  | DataPermissionValue.NO
  | DataPermissionValue.LEGACY_NO_SELF_SERVICE
  | DataPermissionValue.BLOCKED
  | DataPermissionValue.IMPERSONATED
  | {
      [key: SchemaName]: TablesPermissions;
    };

export type TablesPermissions =
  | DataPermissionValue.UNRESTRICTED
  | DataPermissionValue.LEGACY_NO_SELF_SERVICE
  | DataPermissionValue.BLOCKED
  | {
      [key: TableId]: FieldsPermissions;
    };

export type FieldsPermissions =
  | DataPermissionValue.UNRESTRICTED
  | DataPermissionValue.LEGACY_NO_SELF_SERVICE
  | DataPermissionValue.SANDBOXED
  | DataPermissionValue.BLOCKED;

export type UpdateCollectionPermissionsGraphRequest = {
  namespace?: string | null;
  revision: number;
  groups: CollectionPermissions;
};

export type CollectionPermissionsGraph = {
  groups: CollectionPermissions;
  revision: number;
};

export type CollectionPermissions = {
  [key: GroupId | string]: Partial<Record<CollectionId, CollectionPermission>>;
};

export type CollectionPermission = "write" | "read" | "none";

export type GroupTableAccessPolicy = {
  id: number;
  group_id: number;
  table_id: number;
  card_id: number | null;
  attribute_remappings: {
    [key: UserAttributeKey]: ParameterTarget;
  };
  permission_id: number | null;
};

export type Impersonation = {
  db_id: DatabaseId;
  group_id: GroupId;
  attribute: UserAttributeKey;
};

export type DataSegregationStrategy =
  | "row-column-level-security"
  | "connection-impersonation"
  | "database-routing";

export type DatabaseEntityId = {
  databaseId: number;
};

export type SchemaEntityId = DatabaseEntityId & {
  schemaName: string | undefined;
};

export type TableEntityId = SchemaEntityId & {
  tableId: number;
};

export type PermissionEntityId = DatabaseEntityId &
  Partial<Omit<TableEntityId, "databaseId">>;

export type EntityWithGroupId = PermissionEntityId & { groupId: number };

/**
 * A database as the permissions tree sees it: the API database, with `tables`
 * from `GET /api/database/:id/metadata` and `router_user_attribute` from
 * `GET /api/database`. Neither endpoint returns both.
 */
export type PermissionsDatabase = Database;

export type PermissionSubject = "schemas" | "tables" | "fields";

export type SpecialGroupType = "admin" | "analyst" | "external" | null;
